import { ReactElement } from 'react';
import styled from 'styled-components';
import Button from '@sdk/components/ui/buttons/Button';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import View from '@sdk/components/primitives/View';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import useAuth from '@sdk/authentication/useAuth';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import type { $rootScopeType } from '../../common/types/AngularTypes';
import { setDefaultClientFlags } from '../../../src/components/security/userDefaults';
import { SETTINGS_STAGING_BG_COLOR } from '../constants';

import SettingsSection from './SettingsSection';
import RowSpaceBetween from './RowSpaceBetween';

const Container = styled(Column)`
  background: ${SETTINGS_STAGING_BG_COLOR};
`;

const ButtonContainer = styled(View)`
  display: flex;
  min-width: 205px;
  padding-left: 20px;
`;

interface Props {
  $rootScope: $rootScopeType;
}

export default function StagingSection({ $rootScope }: Props): ReactElement {
  const api = useSlingApi();
  const { showInfoToast } = useToast();
  const { currentUser } = useAuth();

  const onResetSubscriptionData = async (): Promise<void> => {
    const { data } = await api.auth.resetSubscriptionData();

    showInfoToast(data);
    window.location.reload();
  };

  const resetClientFlags = async (): Promise<void> => {
    const currentUserCopy = { ...currentUser };
    currentUserCopy.settings.clientFlags = null;
    setDefaultClientFlags(currentUserCopy);

    const { data } = await api.auth.updateSettings(currentUserCopy.settings);
    $rootScope.$emit('sdk:userSettings:save', data);
  };

  return (
    <Container mTop={-20}>
      <SettingsSection pTop={20}>
        <Text14 themeColor={ThemeColorType.gray}>
          {`These settings are only available on staging for testing purposes.
            They won't be visible on production.`}
        </Text14>

        <Column pBottom={20} pTop={20}>
          <RowSpaceBetween>
            <Column>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {'Reset Sling Premium history'}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {`Delete all subscription data whatsoever. You'll be back at square one where you can
              start with a 15-days trial`}
              </Text14>
            </Column>
            <ButtonContainer>
              <ProgressButton onClick={onResetSubscriptionData} buttonType={ButtonType.warning}>
                Reset subscription data
              </ProgressButton>
            </ButtonContainer>
          </RowSpaceBetween>
        </Column>

        <Column pBottom={20}>
          <Button
            buttonType={ButtonType.danger}
            onClick={() => ($rootScope.forceNpsDisplay = true)}>
            {'Display nps survey'}
          </Button>
        </Column>

        <Column pBottom={20}>
          <Button
            buttonType={ButtonType.danger}
            onClick={() => ($rootScope.forcePsatDisplay = true)}>
            {'Display psat survey'}
          </Button>
        </Column>

        <Column pBottom={20}>
          <Button
            buttonType={ButtonType.danger}
            onClick={() => ($rootScope.forceHappinessSurveyDisplay = true)}>
            {'Display happiness survey'}
          </Button>
        </Column>

        <Column pBottom={20}>
          <Button
            buttonType={ButtonType.danger}
            onClick={() => ($rootScope.forceSubscriptionPreferenceDisplay = true)}>
            {'Display subscription preference survey'}
          </Button>
        </Column>

        <Column pBottom={20}>
          <ProgressButton buttonType={ButtonType.danger} onClick={resetClientFlags}>
            {'Reset client flags'}
          </ProgressButton>
        </Column>
      </SettingsSection>
      <HorizontalSeparator />
    </Container>
  );
}
